import { parseRule } from '@op/components/FilterCommon/widgets/KeyWordsSelect/utils';
import { useCustomInputValue } from '@op/components/FilterCommon/hooks/init';
import { asyncCreateShortUrl } from '@op/components/FilterCommon/utils/historyUtils';
import useSaveForm from '@op/components/GPT/hooks/useSaveForm';
import { useAiSaveName } from '@op/pages/AIAnalysisHub/hooks';
import { getReportSuffix } from '@op/utils/time';
import { useFilterPromptInfo } from '../../../../hook';
import { KeywordUrlTabType } from '@op/pages/AIAnalysisHub/KeywordAndURLAnalysis/types';

interface UseCommonFilterProps {
  tab: KeywordUrlTabType;
  wholeGameInfo: any;
  gameInfo: any;
  promptList: any;
  videoPromptList: any;
  userInfo: any;
  analysisSuffix: string; // 'Keyword Analysis' or 'URL Analysis'
}

/**
 * 提取 KeywordFilter 和 UrlFilter 的共同状态管理逻辑
 */
export function useCommonFilter({
  tab,
  wholeGameInfo,
  gameInfo,
  promptList,
  videoPromptList,
  userInfo,
  analysisSuffix,
}: UseCommonFilterProps) {
  // 生成保存名称
  function getSaveName(timeTitle?: string) {
    return `${analysisSuffix}_${timeTitle} ${getReportSuffix()}`;
  }

  // 保存表单相关状态
  const {
    emailList,
    setShowEmailMessage,
    aiSaveTitle: saveName,
    setAiSaveTitle: setSaveName,
    isSave,
    setIsSave,
    showEmailMessage,
    setEmailList,
  } = useSaveForm({
    userInfo,
    getDefaultSaveName: getSaveName,
  });

  // AI 保存名称相关
  const { filterOnChange } = useAiSaveName({
    getSaveName,
    setSaveName,
  });

  // Prompt 信息相关状态
  const {
    promptInfo,
    setPromptInfo,
    videoPromptInfo,
    commentsPromptInfo,
    keywordPromptInfo,
    visible,
    toggleVisible,
    initPromptInfo,
    modalTitle,
    gptType,
    setGptType,
    remainder,
    total,
    advancedLimitLoading,
    advancedConfirm,
    getPromptInfo,
    resetPromptInfo,
    gptList,
    setGptList,
    realPromptList,
    isAdvanced,
    getPromptRealDetail,
  } = useFilterPromptInfo({
    tab,
    wholeGameInfo,
    gameInfo,
    promptList,
    videoPromptList,
  });

  // 关键词输入相关状态
  const {
    keywordCustomInput,
    setKeywordCustomInput,
    initCustomInput,
    keywordSearchType,
    setKeywordSearchType,
    grammarTrue,
    grammarText,
  } = useCustomInputValue();

  // 初始化自定义输入值
  function initCustomInputValue(data: any) {
    const { keywordCustomInput = '', keywordSearchType = 'exact' } = data;
    initCustomInput(keywordCustomInput);
    setKeywordSearchType('exact');
  }

  // 获取其他信息（关键词相关）
  function getKeywordInfo(isCreateShortid = false) {
    const obj: any = {};
    const keywordList = keywordCustomInput.trim()
      ? parseRule(keywordCustomInput)
      : [];
    
    if (keywordSearchType === 'extend') {
      obj.merge_keywords_list = keywordList;
    } else {
      obj.keywords_list = keywordList;
    }

    if (isCreateShortid) {
      obj.keywordCustomInput = keywordCustomInput;
      obj.keywordSearchType = keywordSearchType;
      obj.keywordActiveId = 'custom';
    }

    return obj;
  }

  // 创建短链接
  async function createShortUrl(
    saveFilter: any,
    errorText: string,
  ): Promise<string | null> {
    return await asyncCreateShortUrl({
      filter: saveFilter,
      gameInfo,
      updateUrl: true,
      errorText,
    });
  }

  return {
    // 保存表单相关
    emailList,
    setShowEmailMessage,
    saveName,
    setSaveName,
    isSave,
    setIsSave,
    showEmailMessage,
    setEmailList,
    filterOnChange,
    
    // Prompt 相关
    promptInfo,
    setPromptInfo,
    videoPromptInfo,
    commentsPromptInfo,
    keywordPromptInfo,
    visible,
    toggleVisible,
    initPromptInfo,
    modalTitle,
    gptType,
    setGptType,
    remainder,
    total,
    advancedLimitLoading,
    advancedConfirm,
    getPromptInfo,
    resetPromptInfo,
    gptList,
    setGptList,
    realPromptList,
    isAdvanced,
    getPromptRealDetail,
    
    // 关键词输入相关
    keywordCustomInput,
    setKeywordCustomInput,
    keywordSearchType,
    setKeywordSearchType,
    grammarTrue,
    grammarText,
    initCustomInputValue,
    
    // 工具函数
    getKeywordInfo,
    createShortUrl,
    getSaveName,
  };
}
